import { useState } from "react";
import { PaymentMethod, PaymentType } from "../../Stripe/StripeOrderDTO.ts";
import {
  STRIPE_PAYMENT_METHOD_LABELS,
  STRIPE_PAYMENT_TYPE_LABELS,
} from "../../Stripe/stripePaymentLabels.ts";
import { FilterActions } from "../../UI/Table/Filters/FilterActions.tsx";
import { CheckboxList } from "../../UI/Table/Filters/CheckboxListFilter.tsx";
import { toggleListValue } from "../../UI/Table/Filters/toggleListValue.ts";
import {
  FailingInstallmentsRange,
  FailureCountUnit,
} from "../OrdersList/OrdersListQuery.ts";

export type StripePaymentFilterValue = {
  paymentMethods?: PaymentMethod[];
  paymentType?: PaymentType;
  failingInstallments?: FailingInstallmentsRange;
};

const PAYMENT_METHOD_OPTIONS = Object.values(PaymentMethod).map((method) => ({
  value: method,
  label: STRIPE_PAYMENT_METHOD_LABELS[method],
}));

const PAYMENT_TYPE_OPTIONS: { value?: PaymentType; label: string }[] = [
  { value: undefined, label: "Tutte" },
  ...Object.values(PaymentType).map((type) => ({
    value: type,
    label: STRIPE_PAYMENT_TYPE_LABELS[type],
  })),
];

const FAILURE_COUNT_UNIT_LABELS: Record<FailureCountUnit, string> = {
  attempts: "Tentativi falliti",
  installments: "Rate non pagate",
};

const countInputClassName =
  "w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700";

function SectionTitle({ children }: { children: string }) {
  return <p className="px-1 text-xs font-medium text-gray-500">{children}</p>;
}

function parseCount(input: string): number | undefined {
  const count = Number(input);
  return input.trim() && Number.isInteger(count) && count > 0
    ? count
    : undefined;
}

export function StripePaymentFilter({
  value,
  onApply,
  close,
}: {
  value: StripePaymentFilterValue;
  onApply: (value: StripePaymentFilterValue) => void;
  close: () => void;
}) {
  const [paymentMethods, setPaymentMethods] = useState(
    value.paymentMethods ?? [],
  );
  const [paymentType, setPaymentType] = useState(value.paymentType);
  const [isFailureFilterEnabled, setIsFailureFilterEnabled] = useState(
    !!value.failingInstallments,
  );
  const [failureCountUnit, setFailureCountUnit] = useState<FailureCountUnit>(
    value.failingInstallments?.countBy ?? "attempts",
  );
  const [minFailures, setMinFailures] = useState(
    value.failingInstallments?.min?.toString() ?? "",
  );
  const [maxFailures, setMaxFailures] = useState(
    value.failingInstallments?.max?.toString() ?? "",
  );
  const [rangeError, setRangeError] = useState<string | null>(null);

  const apply = (applied: StripePaymentFilterValue) => {
    onApply(applied);
    close();
  };

  const toFailingInstallmentsRange = ():
    | FailingInstallmentsRange
    | undefined
    | string => {
    if (!isFailureFilterEnabled) return undefined;
    const min = parseCount(minFailures);
    const max = parseCount(maxFailures);
    if (
      (minFailures.trim() && min === undefined) ||
      (maxFailures.trim() && max === undefined)
    )
      return "Inserisci numeri interi maggiori di zero";
    if (min !== undefined && max !== undefined && min > max)
      return "Il valore minimo non può superare il massimo";
    return { countBy: failureCountUnit, min: min ?? 1, max };
  };

  const applySelection = () => {
    const failingInstallments = toFailingInstallmentsRange();
    if (typeof failingInstallments === "string") {
      setRangeError(failingInstallments);
      return;
    }
    apply({
      paymentMethods: paymentMethods.length ? paymentMethods : undefined,
      paymentType,
      failingInstallments,
    });
  };

  return (
    <div className="flex flex-col gap-2 px-2">
      <SectionTitle>Tipologia</SectionTitle>
      <div className="flex flex-col gap-0.5">
        {PAYMENT_TYPE_OPTIONS.map((option) => (
          <label
            key={option.label}
            className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm text-gray-700 hover:bg-gray-50"
          >
            <input
              type="radio"
              name="payment-type-filter"
              checked={paymentType === option.value}
              onChange={() => setPaymentType(option.value)}
              className="w-4 h-4 shrink-0 accent-primary"
            />
            {option.label}
          </label>
        ))}
      </div>
      <hr className="border-gray-100" />
      <label className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50">
        <input
          type="checkbox"
          checked={isFailureFilterEnabled}
          onChange={(e) => {
            setRangeError(null);
            setIsFailureFilterEnabled(e.target.checked);
          }}
          className="w-4 h-4 shrink-0 accent-primary"
        />
        Filtra per rate fallite
      </label>
      {isFailureFilterEnabled && (
        <div className="flex flex-col gap-2 rounded-md bg-gray-50 p-2">
          <div className="flex gap-3">
            {(Object.keys(FAILURE_COUNT_UNIT_LABELS) as FailureCountUnit[]).map(
              (unit) => (
                <label
                  key={unit}
                  className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700"
                >
                  <input
                    type="radio"
                    name="failure-count-unit"
                    checked={failureCountUnit === unit}
                    onChange={() => setFailureCountUnit(unit)}
                    className="w-4 h-4 shrink-0 accent-primary"
                  />
                  {FAILURE_COUNT_UNIT_LABELS[unit]}
                </label>
              ),
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              aria-label="Rate fallite da"
              placeholder="Da (1)"
              value={minFailures}
              onChange={(e) => {
                setRangeError(null);
                setMinFailures(e.target.value);
              }}
              className={countInputClassName}
            />
            <input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              aria-label="Rate fallite a"
              placeholder="A (nessun limite)"
              value={maxFailures}
              onChange={(e) => {
                setRangeError(null);
                setMaxFailures(e.target.value);
              }}
              className={countInputClassName}
            />
          </div>
          <p className="text-xs text-gray-500">
            Contati dall'ultima rata pagata, solo sui piani a rate in corso.
            Stripe ritenta ogni rata fino a 9 volte prima di spostarla in coda
            al piano.
          </p>
          {rangeError && <p className="text-xs text-red-500">{rangeError}</p>}
        </div>
      )}
      <hr className="border-gray-100" />
      <SectionTitle>Metodo</SectionTitle>
      <CheckboxList
        options={PAYMENT_METHOD_OPTIONS}
        selected={paymentMethods}
        onToggle={(method) =>
          setPaymentMethods((current) => toggleListValue(current, method))
        }
      />
      <FilterActions
        canClear={
          !!value.paymentMethods?.length ||
          !!value.paymentType ||
          !!value.failingInstallments
        }
        onClear={() => apply({})}
        onApply={applySelection}
      />
    </div>
  );
}
